.split {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* === Value-props (hero) pane === */

/* A dark teal "letterhead" panel: brand lockup, the hero statement, and the three
   qualities as a gilt-separated line. A faint top-light → bottom-deep gradient
   plus a cropped monogram give the flat teal some depth. */
.hero {
  position: relative;
  overflow: hidden;
  background-color: var(--app-hero-bg);
  background-image: linear-gradient(
    155deg,
    rgba(255, 255, 255, 0.06) 0%,
    rgba(255, 255, 255, 0) 44%,
    rgba(0, 0, 0, 0.16) 100%
  );
  color: var(--app-hero-text);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: var(--mantine-spacing-xl) var(--mantine-spacing-lg);
  min-height: rem(220px);
}

/* Oversized initial, gilt, barely-there — sits behind the content (z-index) and
   is cropped by the panel edge for an engraved, heritage feel. */
.monogram {
  position: absolute;
  right: rem(-32px);
  bottom: rem(-72px);
  font-family: var(--app-font-display);
  font-weight: 700;
  font-size: rem(280px);
  line-height: 1;
  color: var(--color-accent);
  opacity: 0.06;
  pointer-events: none;
  user-select: none;
}

.heroInner {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: var(--mantine-spacing-xl);
  max-width: rem(460px);
}

/* Brand lockup: wordmark over a short gilt rule, like an engraved letterhead. */
.brand {
  display: flex;
  flex-direction: column;
  gap: rem(12px);
}

.brandName {
  font-family: var(--app-font-display);
  font-weight: 700;
  font-size: rem(22px);
  letter-spacing: 0.01em;
  color: var(--app-hero-text);
}

.brandRule {
  width: rem(40px);
  height: rem(2px);
  border-radius: var(--radius-pill);
  background: var(--color-accent);
}

.heroContent {
  display: flex;
  flex-direction: column;
  gap: var(--mantine-spacing-lg);
}

/* The hero statement: the heading face (Libre Baskerville, roman) on two
   intentional lines (the <br/> forces the break after the comma). */
.tagline {
  font-family: var(--app-font-display);
  font-style: normal;
  font-weight: 700;
  font-size: rem(28px);
  line-height: 1.25;
  margin: 0;
  color: var(--app-hero-text);
}

/* The three outcomes as gilt-outlined chips beneath the statement. */
.metricChips {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: rem(10px);
}

.metricChip {
  padding: rem(7px) rem(14px);
  border: rem(1px) solid var(--color-accent);
  border-radius: var(--radius-pill);
  font-family: var(--mantine-font-family);
  font-size: rem(12px);
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--app-hero-text);
}

/* The three qualities on one line, separated by gilt dots — a compact "prospectus"
   tagline rather than a bulleted list. */
.qualities {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.quality {
  font-family: var(--mantine-font-family);
  font-size: rem(12px);
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--app-hero-text);
  opacity: 0.88;
}

.quality + .quality::before {
  content: '';
  display: inline-block;
  width: rem(4px);
  height: rem(4px);
  margin: 0 rem(14px);
  border-radius: 50%;
  background: var(--color-accent);
  vertical-align: middle;
}

/* === Form pane === */

.formPane {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--mantine-color-body);
  padding: var(--mantine-spacing-xl) var(--mantine-spacing-md);
}

.formContainer {
  width: 100%;
  max-width: rem(420px);
}

/* === Desktop (1280px+): side-by-side split === */

@media (min-width: $mantine-breakpoint-lg) {
  .split {
    flex-direction: row;
  }

  .hero {
    width: 44%;
    min-height: 100vh;
    padding: rem(72px) rem(56px);
    position: sticky;
    top: 0;
  }

  /* On the tall panel the brand pins to the top; the statement + qualities take
     the remaining height and centre within it (editorial split). */
  .heroInner {
    flex: 1;
    max-width: rem(440px);
  }

  .heroContent {
    flex: 1;
    justify-content: center;
  }

  .monogram {
    font-size: rem(360px);
    right: rem(-48px);
    bottom: rem(-96px);
  }

  .tagline {
    font-size: rem(36px);
  }
}
